/*
 * <cart-upsell-item>
 * Drives a single cart-drawer upsell card. Each product option is a group
 * carrying [data-option-index] + [data-selected-value]; the input itself may be
 * a colour-swatch dropdown, a row of size buttons, or a plain <select>. The
 * element resolves the chosen options to a variant, keeps price/availability in
 * sync, and feeds the variant id to the theme's existing <add-to-cart> element
 * (which re-renders the open drawer in place — no reopen/close).
 */
if (!customElements.get('cart-upsell-item')) {
  class CartUpsellItem extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.addButton = this.querySelector('add-to-cart');
      this.label = this.querySelector('.cart-upsell__add-label');
      this.priceEl = this.querySelector('[data-upsell-price]');
      this.addText = this.dataset.addText || (this.label && this.label.textContent) || 'Add';
      this.soldOutText = this.dataset.soldoutText || '';

      this.optionGroups = Array.from(this.querySelectorAll('[data-option-index]')).sort(
        (a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex)
      );

      this.variants = this.parseJSON('[data-upsell-variants]', []);
      this.prices = this.parseJSON('[data-upsell-prices]', {});
      this.images = this.parseJSON('[data-upsell-images]', {});
      this.imageEl = this.querySelector('.cart-upsell__image img');

      this.onDocClick = this.onDocClick.bind(this);

      // Plain <select> options.
      this.querySelectorAll('.cart-upsell__select').forEach((sel) => {
        sel.addEventListener('change', () => {
          this.setSelected(sel.closest('[data-option-index]'), sel.value);
        });
      });

      // Size buttons + swatch options (event delegation).
      this.addEventListener('click', (e) => this.onClick(e));

      this.onChange();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    parseJSON(selector, fallback) {
      const el = this.querySelector(selector);
      if (!el) return fallback;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return fallback;
      }
    }

    onClick(e) {
      const toggle = e.target.closest('.cu-swatch-dd__toggle');
      if (toggle && this.contains(toggle)) {
        this.toggleDropdown(toggle.closest('.cu-swatch-dd'));
        return;
      }

      const swatchOpt = e.target.closest('.cu-swatch-opt');
      if (swatchOpt && this.contains(swatchOpt)) {
        const dd = swatchOpt.closest('.cu-swatch-dd');
        const group = swatchOpt.closest('[data-option-index]');
        dd.querySelectorAll('.cu-swatch-opt').forEach((o) => {
          const on = o === swatchOpt;
          o.classList.toggle('is-selected', on);
          o.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        // Reflect the chosen swatch on the toggle.
        const current = dd.querySelector('.cu-swatch-dd__toggle .cu-swatch');
        const chosen = swatchOpt.querySelector('.cu-swatch');
        if (current && chosen) current.setAttribute('style', chosen.getAttribute('style') || '');
        this.setSelected(group, swatchOpt.dataset.value);
        this.toggleDropdown(dd, false);
      }
    }

    toggleDropdown(dd, force) {
      if (!dd) return;
      const list = dd.querySelector('.cu-swatch-dd__list');
      const toggle = dd.querySelector('.cu-swatch-dd__toggle');
      const open = typeof force === 'boolean' ? force : list.hasAttribute('hidden');

      // Close any other open dropdowns first.
      this.querySelectorAll('.cu-swatch-dd__list:not([hidden])').forEach((l) => {
        if (l !== list) {
          l.setAttribute('hidden', '');
          const t = l.closest('.cu-swatch-dd').querySelector('.cu-swatch-dd__toggle');
          if (t) t.setAttribute('aria-expanded', 'false');
        }
      });

      if (open) {
        list.removeAttribute('hidden');
        toggle.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocClick);
      } else {
        list.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocClick);
      }
    }

    onDocClick(e) {
      if (!e.target.closest('.cu-swatch-dd')) {
        this.querySelectorAll('.cu-swatch-dd__list:not([hidden])').forEach((l) => {
          l.setAttribute('hidden', '');
          const t = l.closest('.cu-swatch-dd').querySelector('.cu-swatch-dd__toggle');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
        document.removeEventListener('click', this.onDocClick);
      }
    }

    setSelected(group, value) {
      if (!group) return;
      group.dataset.selectedValue = value;
      this.onChange();
    }

    getSelectedVariant() {
      if (!this.optionGroups.length) {
        return (
          this.variants.find(
            (v) => String(v.id) === String(this.addButton && this.addButton.dataset.variantId)
          ) || this.variants[0]
        );
      }
      const chosen = this.optionGroups.map((g) => g.dataset.selectedValue);
      return this.variants.find((v) =>
        chosen.every((val, i) => String(v.options[i]) === String(val))
      );
    }

    onChange() {
      if (!this.addButton) return;
      const variant = this.getSelectedVariant();

      // Swap the card image to the selected variant (e.g. on colour change).
      if (variant && this.imageEl && this.images[variant.id]) {
        this.imageEl.src = this.images[variant.id];
        this.imageEl.removeAttribute('srcset');
      }

      if (variant && variant.available) {
        this.addButton.dataset.variantId = variant.id;
        this.addButton.classList.remove('is-unavailable');
        this.addButton.removeAttribute('aria-disabled');
        if (this.label && this.addText) this.label.textContent = this.addText;
        if (this.priceEl && this.prices[variant.id] != null) {
          this.priceEl.innerHTML = this.prices[variant.id];
        }
      } else {
        // Empty id makes the existing <add-to-cart> a no-op on click.
        this.addButton.dataset.variantId = '';
        this.addButton.classList.add('is-unavailable');
        this.addButton.setAttribute('aria-disabled', 'true');
        if (this.label && this.soldOutText) this.label.textContent = this.soldOutText;
      }
    }
  }

  customElements.define('cart-upsell-item', CartUpsellItem);
}
